import React, { cloneElement, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import type { ReactElement } from 'react';
import { copyToClipboard } from '../../utils/copyToClipboard';

/** Same focus-trap roots as copyToClipboard.resolveMountPoint. */
const FOCUS_TRAP_SELECTOR =
  '[role="dialog"], .MuiModal-root, .MuiPopover-root, .MuiDialog-root';

interface CopyWithTooltipProps {
  value: string;
  children: ReactElement;
  tooltipTitle?: string;
  tooltipPlacement?: 'bottom' | 'top' | 'left' | 'right';
  duration?: number;
}

/**
 * Component that wraps a child element and adds copy-to-clipboard functionality
 * with an animated "Copied!" tooltip that appears after a successful copy.
 *
 * @param {string} value - The text value to copy to clipboard
 * @param {ReactElement} children - The child element that will trigger the copy action
 * @param {string} tooltipTitle - Optional custom tooltip text (default: "Copied!")
 * @param {string} tooltipPlacement - Optional tooltip placement (default: "top")
 * @param {number} duration - Optional tooltip display duration in ms (default: 1000)
 * @returns {JSX.Element}
 */
const CopyWithTooltip: React.FC<CopyWithTooltipProps> = ({
  value,
  children,
  tooltipTitle = 'Copied!',
  tooltipPlacement = 'top',
  duration = 1000,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();

    // Prefer mounting the execCommand textarea inside the nearest MUI
    // dialog/popover so the focus trap cannot steal selection mid-copy.
    const target = event.currentTarget as HTMLElement | null;
    const container =
      (target?.closest?.(FOCUS_TRAP_SELECTOR) as HTMLElement | null) ?? null;

    const succeeded = await copyToClipboard(value, { container });
    if (!succeeded) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setTooltipOpen(true);
    timeoutRef.current = setTimeout(() => {
      setTooltipOpen(false);
      timeoutRef.current = null;
    }, duration);
  };

  const { title, ...restProps } = children.props;
  const childWithClick = cloneElement(children, {
    ...restProps,
    onClick: (e: React.MouseEvent) => {
      void handleCopy(e);
      if (children.props.onClick) {
        children.props.onClick(e);
      }
    },
  });

  return (
    <Tooltip
      open={tooltipOpen}
      title={tooltipTitle}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement={tooltipPlacement}
      arrow
    >
      <span style={{ display: 'inline-flex' }}>{childWithClick}</span>
    </Tooltip>
  );
};

export default CopyWithTooltip;
